import classNames from "classnames";
import React, { FC, FormEvent, ReactNode, useState } from "react";
import { createPortal } from "react-dom";

import {
  TwoStepVerifyMethod,
  useDisableTwoStepMutation,
  useForgetTwoStepDevicesMutation,
  useGetTwoStepSettingsQuery,
  useNewRecoveryCodesMutation,
  useSendTwoStepCodeMutation,
} from "../../slices/TwoStepSlice";
import {
  isCompleteCode,
  methodLabel,
  twoStepErrorMessage,
} from "./twoStep.utils";
import { TwoStepSetup } from "./TwoStepSetup";
import { saveDeviceToken } from "./twoStepStorage";
import {
  CodeInput,
  RecoveryCodesPanel,
  ShieldIcon,
  TsAlert,
  tsLink,
  tsPrimary,
  tsSecondary,
  tsSmallPrimary,
} from "./TwoStepUi";

const CARD =
  "mb-5 rounded-xl border border-[#E7E9EF] bg-white px-[26px] py-[22px] shadow-[0_1px_2px_rgba(20,22,34,0.04)]";

/** A simple centered window (portal) for the profile's two-step actions. */
export const TwoStepModal: FC<{
  title: string;
  onClose: () => void;
  children: ReactNode;
}> = ({ title, onClose, children }) =>
  createPortal(
    <div
      className={
        "fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-[rgba(17,20,32,0.45)] px-4 py-8 font-[Inter,sans-serif] text-[#1F2433]"
      }
      role={"dialog"}
      aria-modal={"true"}
      aria-label={title}
      onKeyDown={(event) => event.key === "Escape" && onClose()}>
      <div
        className={
          "my-auto flex w-full max-w-[480px] flex-col gap-4 rounded-[14px] bg-white p-6 shadow-[0_20px_60px_rgba(17,20,32,0.25)]"
        }>
        <div className={"flex items-start justify-between gap-4"}>
          <span className={"text-[17px] font-extrabold"}>{title}</span>
          <button
            type={"button"}
            aria-label={"Close"}
            onClick={onClose}
            className={
              "flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-xl leading-none text-[#8A90A2] hover:bg-[#F5F6FA]"
            }>
            ×
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );

type Dialog = "setup" | "disable" | "codes" | null;

/**
 * Profile page: "Two-step sign-in" card. Turn on (authenticator app or email),
 * change method, new recovery codes, turn off (needs a current code; not while
 * a firm requires it), and "Signed-in computers you trust: Forget all".
 */
export const TwoStepProfileCard: FC<{ email: string }> = ({ email }) => {
  const { data, isLoading, isError } = useGetTwoStepSettingsQuery();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [forget, forgetState] = useForgetTwoStepDevicesMutation();
  const [newCodes, newCodesState] = useNewRecoveryCodesMutation();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) return null;
  if (isError || !data?.available) {
    return (
      <div className={CARD}>
        <CardTitle />
        <div className={"mt-2 text-[13px] font-semibold text-[#8A90A2]"}>
          Two-step sign-in isn&apos;t available on this server yet.
        </div>
      </div>
    );
  }

  const required = data.requiredBy.length > 0;

  const forgetAll = async () => {
    setError(null);
    const result = await forget();
    if ("error" in result) return setError(twoStepErrorMessage(result.error));
    saveDeviceToken(email, null);
    setMessage("Done. Every computer will ask for a code at the next sign-in.");
  };

  const makeCodes = async () => {
    setError(null);
    const result = await newCodes();
    if ("error" in result) return setError(twoStepErrorMessage(result.error));
    setCodes(result.data.recoveryCodes || []);
    setDialog("codes");
  };

  return (
    <div className={CARD} data-testid={"two-step-card"}>
      <div className={"flex items-start justify-between gap-4"}>
        <div className={"flex flex-col gap-1"}>
          <CardTitle />
          <span className={"text-[13.5px] leading-[1.5] text-[#5F6577]"}>
            After your password, ProCharted asks for a 6-digit code from an
            authenticator app or your email, so nobody else can sign in with just
            your password.
          </span>
        </div>
        <span
          className={classNames(
            "shrink-0 rounded-full px-2.5 py-1 text-[12px] font-bold",
            data.enabled
              ? "bg-[#E9F9F1] text-[#147A4B]"
              : "bg-[#F1F2F5] text-[#6B7185]",
          )}>
          {data.enabled ? "On" : "Off"}
        </span>
      </div>

      {required && (
        <div className={"mt-3"}>
          <TsAlert tone={"info"}>
            {data.requiredBy.join(", ")} requires two-step sign-in for everyone.
          </TsAlert>
        </div>
      )}

      {data.enabled ? (
        <div className={"mt-4 flex flex-col gap-3.5"}>
          <Row label={"Method"}>
            <span className={"text-sm font-bold"}>
              {methodLabel(data.method)}
            </span>
            <button
              type={"button"}
              className={tsLink}
              onClick={() => setDialog("setup")}>
              Change
            </button>
          </Row>
          <Row label={"Recovery codes"}>
            <span className={"text-sm font-bold"}>
              {data.recoveryCodesLeft} of 8 left
            </span>
            <button
              type={"button"}
              className={tsLink}
              disabled={newCodesState.isLoading}
              onClick={() => void makeCodes()}>
              Make new codes
            </button>
          </Row>
          <Row label={"Signed-in computers you trust"}>
            <span className={"text-sm font-bold"}>
              {data.trustedDevices === 0
                ? "None"
                : data.trustedDevices === 1
                ? "1 computer"
                : `${data.trustedDevices} computers`}
            </span>
            <button
              type={"button"}
              className={tsLink}
              disabled={forgetState.isLoading || data.trustedDevices === 0}
              onClick={() => void forgetAll()}>
              Forget all
            </button>
          </Row>
          {message && <TsAlert tone={"success"}>{message}</TsAlert>}
          {error && <TsAlert>{error}</TsAlert>}
          <div>
            {required ? (
              <span className={"text-[12.5px] text-[#8A90A2]"}>
                It can&apos;t be turned off while your firm requires it.
              </span>
            ) : (
              <button
                type={"button"}
                className={`${tsSecondary} text-[#B42318]`}
                onClick={() => setDialog("disable")}>
                Turn off
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className={"mt-4"}>
          <button
            type={"button"}
            className={tsSmallPrimary}
            onClick={() => setDialog("setup")}>
            Turn on two-step sign-in
          </button>
        </div>
      )}

      {dialog === "setup" && (
        <TwoStepModal
          title={
            data.enabled ? "Change two-step method" : "Turn on two-step sign-in"
          }
          onClose={() => setDialog(null)}>
          <TwoStepSetup
            email={data.email || email}
            current={data.enabled ? data.method : null}
            onDone={() => setDialog(null)}
            onCancel={() => setDialog(null)}
          />
        </TwoStepModal>
      )}
      {dialog === "disable" && (
        <TwoStepModal
          title={"Turn off two-step sign-in"}
          onClose={() => setDialog(null)}>
          <DisableForm
            method={data.method === "email" ? "email" : "app"}
            onDone={() => {
              setDialog(null);
              setMessage(null);
            }}
          />
        </TwoStepModal>
      )}
      {dialog === "codes" && codes && (
        <TwoStepModal
          title={"Your new recovery codes"}
          onClose={() => setDialog(null)}>
          <span className={"text-[13px] leading-[1.55] text-[#5B6678]"}>
            Your old recovery codes no longer work. Save these somewhere safe:
            they are shown only now.
          </span>
          <RecoveryCodesPanel codes={codes} email={email} />
          <button
            type={"button"}
            className={tsPrimary}
            onClick={() => setDialog(null)}>
            I&apos;ve saved my codes
          </button>
        </TwoStepModal>
      )}
    </div>
  );
};

const CardTitle = () => (
  <span
    className={
      "flex items-center gap-2 text-base font-extrabold text-[#1F2433]"
    }>
    <span className={"text-[#6B4EFF]"}>
      <ShieldIcon size={17} />
    </span>
    Two-step sign-in
  </span>
);

const Row: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div
    className={
      "flex flex-wrap items-center justify-between gap-2 border-t border-[#F0F1F4] pt-3.5"
    }>
    <span className={"text-[13px] font-semibold text-[#8A90A2]"}>{label}</span>
    <span className={"flex items-center gap-4"}>{children}</span>
  </div>
);

/** Turning off needs a current code (app, emailed, or a recovery code). */
const DisableForm: FC<{ method: "app" | "email"; onDone: () => void }> = ({
  method: startMethod,
  onDone,
}) => {
  const [method, setMethod] = useState<TwoStepVerifyMethod>(startMethod);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [disable, disableState] = useDisableTwoStepMutation();
  const [sendCode, sendState] = useSendTwoStepCodeMutation();
  const recovery = method === "recovery";

  const emailMe = async () => {
    setError(null);
    const result = await sendCode({ purpose: "disable" });
    if ("error" in result) return setError(twoStepErrorMessage(result.error));
    setMethod("email");
    setCode("");
    setNotice(`We sent a code to ${result.data.sentTo}.`);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isCompleteCode(code, recovery))
      return setError(
        recovery ? "Enter the whole recovery code." : "Enter the 6-digit code.",
      );
    setError(null);
    const result = await disable({ code, method });
    if ("error" in result) return setError(twoStepErrorMessage(result.error));
    onDone();
  };

  return (
    <form className={"flex flex-col gap-4"} onSubmit={submit}>
      <span className={"text-[13.5px] leading-[1.55] text-[#5B6678]"}>
        To make sure it&apos;s you, enter a current code.{" "}
        {method === "app" && "Use the code from your authenticator app."}
        {method === "email" && !notice && "Click “Email me a code” first."}
      </span>
      {notice && <TsAlert tone={"info"}>{notice}</TsAlert>}
      <CodeInput
        key={method}
        id={"two-step-disable-code"}
        value={code}
        onChange={setCode}
        recovery={recovery}
        autoFocus={true}
      />
      {error && <TsAlert>{error}</TsAlert>}
      <button
        type={"submit"}
        className={`${tsPrimary} bg-[#D92D20] shadow-none hover:bg-[#B42318]`}
        disabled={disableState.isLoading}>
        {disableState.isLoading ? "Turning off…" : "Turn off two-step sign-in"}
      </button>
      <div className={"flex flex-wrap gap-4"}>
        <button
          type={"button"}
          className={tsLink}
          onClick={() => void emailMe()}
          disabled={sendState.isLoading}>
          {method === "email" && notice ? "Send a new code" : "Email me a code"}
        </button>
        {!recovery && (
          <button
            type={"button"}
            className={tsLink}
            onClick={() => {
              setMethod("recovery");
              setCode("");
            }}>
            Use a recovery code
          </button>
        )}
      </div>
    </form>
  );
};
